/* This component holds the state for a single publication and places it in Zustand. */

import { isEqual } from "lodash";
import { createContext, useContext, useEffect, useMemo, useRef } from "react";
import { FormattedMessage } from "react-intl";

import {
  LayersInnerFromJSON,
  LayersInnerToJSON,
  MapCreateFromJSON,
  MapCreateToJSON,
  PublicationReadDetail,
  PublicationVersionRead,
} from "@nens/rana/api-v1";
import { RanaLoading, TenantContext } from "@nens/ranalib";

import { PublicationDetail } from "../features/detail";
import { usePublicationVersion } from "../hooks/use-version";
import { PublicationContext, createPublicationStore } from "../store/store";
import { combinePublicationAndVersion } from "../store/transformers";
import { Publication, PublicationMap } from "../types/publication";

import { ResetPublicationModal } from "./reset-publication-modal";
import { UnexpectedPublicationStateWarning } from "./unexpected-publication-state-warning";

interface PublicationProps {
  publicationRead: PublicationReadDetail;
  closePublication: () => void;
}

interface PublicationVersionFunctions {
  // Close this publication and go back
  closePublication: () => void;
  // Switch to a different version of this publication
  setPublicationVersion: (publicationVersion: PublicationVersionRead | number) => Promise<void>;
  // Reset the publication version to the initial one (published for public, latest for authenticated users). This is used after saving a new version, to switch to it.
  resetPublicationVersion: (isChanged: boolean) => void;
}

export const PublicationVersionFunctionsContext = createContext<PublicationVersionFunctions>({
  resetPublicationVersion: () => undefined,
  closePublication: () => undefined,
  setPublicationVersion: () => Promise.resolve(),
});

export function PublicationStoreWrapper({ closePublication, publicationRead }: PublicationProps) {
  // This wrapper component keeps track of *which version* of the publication we are looking at.
  // Initially this is either "published" (for public access) or "latest" (for authenticated access),
  // later an authenticated user can switch to another version.

  // It also places functions in a context for leaving the publication or choosing a new version

  // Fetch the right initial publication version
  const { isPublic } = useContext(TenantContext);

  const {
    isLoading,
    resetCounter,
    publicationVersion,
    latestVersion,
    setPublicationVersion,
    resetPublicationModalOpen,
    setResetPublicationModalOpen,
    resetPublicationVersion,
  } = usePublicationVersion(publicationRead);

  // Memoize publication so the store only resets when data actually changes (or reset is
  // explicitly requested), not on every render of this component.
  const publication = useMemo(() => {
    if (!publicationVersion) return null;

    return combinePublicationAndVersion(
      isPublic,
      publicationRead,
      publicationVersion.publicationVersion,
      publicationVersion.files,
      publicationVersion.descriptors,
      latestVersion
    );
    // Add a resetCounter to the dependencies, so we can reset the store by increasing the counter and thus changing the publication object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicationVersion, isPublic, publicationRead, latestVersion, resetCounter]);

  // If publication becomes null after having a value, it means something went wrong. Show a warning in that case.
  const publicationHasBeenSeen = useRef(false);
  useEffect(() => {
    if (publication) publicationHasBeenSeen.current = true;
  }, [publication]);

  if (!publication) {
    if (isLoading) {
      return (
        <>
          {publicationHasBeenSeen.current && <UnexpectedPublicationStateWarning />}

          <RanaLoading isCentered />
        </>
      );
    }
    return (
      <FormattedMessage id="publication.loading_error" defaultMessage="Error loading publication" />
    );
  }

  return (
    <PublicationVersionFunctionsContext.Provider
      value={{
        closePublication,
        setPublicationVersion,
        resetPublicationVersion: (isChanged: boolean) => {
          isChanged ? setResetPublicationModalOpen(true) : resetPublicationVersion();
        },
      }}
    >
      <ResetPublicationModal
        isOpen={resetPublicationModalOpen}
        reset={resetPublicationVersion}
        onClose={() => setResetPublicationModalOpen(false)}
      />
      <PublicationStoreProvider publication={publication} />
    </PublicationVersionFunctionsContext.Provider>
  );
}

// Inner component, contains the store
const getMapBackendLayers = ({ layers, studyArea }: PublicationMap) =>
  [...layers, ...(studyArea ? [studyArea] : [])].map((layer) =>
    LayersInnerFromJSON(LayersInnerToJSON(layer))
  );

interface PublicationStoreProviderProps {
  publication: Publication;
}

function PublicationStoreProvider({ publication: _publication }: PublicationStoreProviderProps) {
  const prevStoreRef = useRef<ReturnType<typeof createPublicationStore> | null>(null);

  const store = useMemo(() => {
    const prevStore = prevStoreRef.current;
    const publication = { ..._publication };
    if (prevStore) {
      const { maps, openMap } = prevStore.getState();

      const allMapsAreTheSame = isEqual(
        maps.map(getMapBackendLayers),
        publication.maps.map(getMapBackendLayers)
      );

      // If all the maps are the same except for the UI fields, it probably means a save just happened.
      // In that case, we want to keep stuff like the open map, selected layers etc the same.
      if (allMapsAreTheSame) {
        publication.openMap = openMap;
        publication.maps = maps.map((prevMap, idx): typeof prevMap => ({
          ...prevMap,
          // Update map id to the new one, goes from null to the new id after saving for the first time
          ...MapCreateFromJSON(MapCreateToJSON(publication.maps[idx])),
          // But put the layers from the previous map in, so we keep the UI state (selected layers, open groups etc)
          studyArea: prevMap.studyArea,
          layers: prevMap.layers,
        }));
      }
    }

    const store = createPublicationStore(publication);
    prevStoreRef.current = store;
    return store;
  }, [_publication]);

  return (
    <PublicationContext.Provider value={store}>
      <PublicationDetail />
    </PublicationContext.Provider>
  );
}
